import { legendSwatches } from './ProductTableEditorView.styles';
import type {
    ProductTableImage,
    ProductTablePrice,
    ProductTableProduct,
    ProductTableSavePayload,
    ProductTableSaveSummary,
} from './ProductTableEditorView.types';

export type NewProductPriceFormState = {
    label: string;
    price: string;
};

export type NewProductImageFormState = {
    source: string;
    alt: string;
    file?: File;
    previewUrl?: string;
};

export type NewProductFormState = {
    name: string;
    slugOverride: string;
    description: string;
    categoryKey: string;
    inventoryId: string;
    width: string;
    height: string;
    depth: string;
    length: string;
    prices: NewProductPriceFormState[];
    images: NewProductImageFormState[];
    features: string;
    searchTerms: string;
    isFeatured: boolean;
    isClearance: boolean;
    isAusMade: boolean;
    isInStock: boolean;
};

export type ProductBasicRecord = {
    name?: string;
    category?: string;
    subtitle?: string;
    active?: boolean;
};

export type ProductDimensionsRecord = {
    width?: string;
    height?: string;
    depth?: string;
    length?: string;
};

export type ProductAttributesRecord = {
    dimensions?: ProductDimensionsRecord;
};

export type ProductMetaDataRecord = {
    productId?: string;
    inventoryId?: string;
};

export type ProductFlagsRecord = {
    isFeatured?: boolean;
    isClearance?: boolean;
    isAusMade?: boolean;
    isInStock?: boolean;
};

export type ProductFlagKey = keyof ProductFlagsRecord;

export const asObject = (value: unknown): Record<string, unknown> => {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
};

export const getBasic = (product: ProductTableProduct): ProductBasicRecord => {
    return asObject(product.basic) as ProductBasicRecord;
};

export const getAttributes = (product: ProductTableProduct): ProductAttributesRecord => {
    return asObject(product.attributes) as ProductAttributesRecord;
};

export const getDimensions = (product: ProductTableProduct): ProductDimensionsRecord => {
    return asObject(getAttributes(product).dimensions) as ProductDimensionsRecord;
};

export const getMetaData = (product: ProductTableProduct): ProductMetaDataRecord => {
    return asObject(product.metaData) as ProductMetaDataRecord;
};

export const getFlags = (product: ProductTableProduct): ProductFlagsRecord => {
    return asObject(product.flags) as ProductFlagsRecord;
};

export const createEmptyProductForm = (): NewProductFormState => ({
    name: '',
    slugOverride: '',
    description: '',
    categoryKey: '',
    inventoryId: '',
    width: '',
    height: '',
    depth: '',
    length: '',
    prices: [{ label: 'Starting from', price: '' }],
    images: [],
    features: '',
    searchTerms: '',
    isFeatured: false,
    isClearance: false,
    isAusMade: false,
    isInStock: true,
});

export const slugifyValue = (value: string) =>
    value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

export const splitLines = (value: string) => value.split('\n').map((item) => item.trim()).filter(Boolean);

export const getFileExtension = (value: string) => {
    const clean = value.split('?')[0].split('#')[0];
    const match = clean.match(/\.([a-z0-9]+)$/i);
    return match?.[1] || 'webp';
};

export const getFileExtensionFromFile = (file: File) => {
    const nameExtension = getFileExtension(file.name);
    if (nameExtension && nameExtension !== 'webp') return nameExtension.toLowerCase();
    const mimeExtension = file.type?.split('/')[1]?.replace('jpeg', 'jpg');
    return mimeExtension || nameExtension || 'webp';
};

export const getBaseImagePathFromSample = (sample?: string) => {
    if (!sample) return '';
    const withoutFile = sample.replace(/\/[^/]*$/, '');
    if (withoutFile.endsWith('/thumbnails')) return withoutFile.replace(/\/thumbnails$/, '');
    return withoutFile;
};

export const getNextNumberForPrefix = (products: ProductTableProduct[], prefix: string) => {
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const numbers = products
        .map((product) => product.id?.match(new RegExp(`^${escapedPrefix}-(\\d+)$`))?.[1])
        .filter(Boolean)
        .map((value) => Number(value));

    const max = numbers.length ? Math.max(...numbers) : 0;
    return max + 1;
};

export const makeUniqueId = (products: ProductTableProduct[], preferredId: string) => {
    const existingIds = new Set(products.map((product) => product.id));
    if (!existingIds.has(preferredId)) return preferredId;

    let index = 2;
    while (existingIds.has(`${preferredId}-${index}`)) index += 1;
    return `${preferredId}-${index}`;
};

export const getDisplayName = (product: ProductTableProduct) =>
    product.name || getBasic(product).name || product.title || product.slug || product.id;

export const getProductTypeLabel = (product: ProductTableProduct) => {
    const raw = product.productSlug || product.parentId || product.productName || 'type-unavailable';
    return raw
        .replace(/^itm-prd-/, '')
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const getPriceValue = (price?: ProductTablePrice) => price?.price ?? null;

export const formatPrice = (value: number | null | undefined, currency: string) => {
    if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) return 'Contact for price';

    try {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `${currency} ${value}`;
    }
};

export const getDimensionsLabel = (product: ProductTableProduct) => {
    const dims = getDimensions(product);
    const width = dims?.width?.trim() || '—';
    const height = dims?.height?.trim() || '—';
    const depth = dims?.depth?.trim() || '—';
    const length = dims?.length?.trim() || '';
    return length ? `W ${width} · H ${height} · D ${depth} · L ${length}` : `W ${width} · H ${height} · D ${depth}`;
};

export const getPrimaryImage = (product: ProductTableProduct): ProductTableImage | undefined => {
    return product.featureImage || product.images?.[0] || (product.imageUrl
        ? {
            original: product.imageUrl,
            thumbnail: product.imageUrl,
            alt: product.imageAlt || getDisplayName(product),
        }
        : undefined);
};

export const getIsRemoved = (product: ProductTableProduct) => getBasic(product).active === false;

export const getFlagValue = (product: ProductTableProduct, key: ProductFlagKey) => Boolean(getFlags(product)[key]);

export const escapeJsonFileName = (value: string) =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

export const categoryColor = (index: number) => legendSwatches[index % legendSwatches.length];

export const ensureAtLeastOnePrice = (pricing?: ProductTablePrice[]) => {
    if (pricing?.length) return pricing;
    return [{ label: 'Starting from', price: null }];
};

export const ensureAtLeastOneFeature = (features?: string[]) => {
    if (features?.length) return features;
    return [''];
};

export const normalizeProductForDiff = (product: ProductTableProduct) => ({
    ...product,
    pricing: ensureAtLeastOnePrice(product.pricing).map((price) => ({
        label: price.label || '',
        price: price.price ?? null,
    })),
    features: ensureAtLeastOneFeature(product.features).map((feature) => feature || ''),
    searchTerms: (product.searchTerms || []).map((term) => term || ''),
    attributes: {
        ...getAttributes(product),
        dimensions: {
            width: getDimensions(product).width || '',
            height: getDimensions(product).height || '',
            depth: getDimensions(product).depth || '',
            length: getDimensions(product).length || '',
        },
    },
    metaData: {
        ...getMetaData(product),
        productId: getMetaData(product).productId || '',
        inventoryId: getMetaData(product).inventoryId || '',
    },
});

export const getRecordLabel = (product: ProductTableProduct) => {
    const parts = [getDisplayName(product)];
    if (getMetaData(product).inventoryId?.trim()) parts.push(getMetaData(product).inventoryId!.trim());
    if (product.categoryName || getBasic(product).category) parts.push(product.categoryName || getBasic(product).category || '');
    return parts.filter(Boolean).join(' · ');
};

export const stripEditorImageFields = (image: ProductTableImage): ProductTableImage => {
    const {
        sourceFile: _sourceFile,
        previewUrl: _previewUrl,
        ...cleanImage
    } = image;

    return cleanImage;
};

export const cleanProductForSave = (product: ProductTableProduct): ProductTableProduct => {
    const cleanImages = product.images?.map(stripEditorImageFields);
    const cleanFeatureImage = product.featureImage ? stripEditorImageFields(product.featureImage) : undefined;
    const { uploadedImages: _uploadedImages, ...cleanProduct } = product;

    return {
        ...cleanProduct,
        ...(cleanImages ? { images: cleanImages } : {}),
        ...(cleanFeatureImage ? { featureImage: cleanFeatureImage } : {}),
    };
};

export const cleanAddedProductForSave = (product: ProductTableProduct): ProductTableProduct => {
    const cleanProduct = cleanProductForSave(product);
    const uploadedImages = Array.isArray(product.uploadedImages)
        ? product.uploadedImages
        : (product.images || [])
            .map((image) => image.sourceFile)
            .filter((file): file is File => Boolean(file));

    return {
        ...cleanProduct,
        ...(uploadedImages.length ? { uploadedImages } : {}),
    };
};

export const buildProductTableSavePayload = ({
    draftProducts,
    saveSummary,
}: {
    draftProducts: ProductTableProduct[];
    saveSummary: ProductTableSaveSummary;
}): ProductTableSavePayload => {
    const buildSummaryItem = (product: ProductTableProduct) => ({
        id: product.id,
        slug: product.slug,
        name: getDisplayName(product),
        category: product.categoryName || getBasic(product).category || product.parentId || 'Uncategorised',
        label: getRecordLabel(product),
    });

    const productItems = draftProducts.map(cleanProductForSave);
    const edited = saveSummary.edited.map(cleanProductForSave);
    const removed = saveSummary.removed.map(cleanProductForSave);
    const added = saveSummary.added.map(cleanAddedProductForSave);

    return {
        productItems,
        products: productItems,
        changes: {
            edited,
            removed,
            added,
        },
        summary: {
            title: 'Save database summary',
            createdAt: new Date().toISOString(),
            counts: {
                edited: edited.length,
                removed: removed.length,
                added: added.length,
                total: saveSummary.total,
            },
            editedRecords: edited.map(buildSummaryItem),
            removedRecords: removed.map(buildSummaryItem),
            addedRecords: added.map(buildSummaryItem),
        },
    };
};

const downloadJson = (fileName: string, payload: unknown) => {
    const dataBlob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const dataUrl = URL.createObjectURL(dataBlob);
    const dataAnchor = document.createElement('a');
    dataAnchor.href = dataUrl;
    dataAnchor.download = fileName;
    document.body.appendChild(dataAnchor);
    dataAnchor.click();
    dataAnchor.remove();
    URL.revokeObjectURL(dataUrl);
};

export const downloadProductTableDebugFiles = ({
    payload,
    draftProducts,
    fileNamePrefix,
}: {
    payload: ProductTableSavePayload;
    draftProducts: ProductTableProduct[];
    fileNamePrefix: string;
}) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const safePrefix = escapeJsonFileName(fileNamePrefix);

    downloadJson(`${safePrefix}-records-${timestamp}.json`, {
        productItems: payload.productItems,
    });

    downloadJson(`${safePrefix}-save-summary-${timestamp}.json`, payload.summary);

    draftProducts.forEach((product) => {
        product.images?.forEach((image) => {
            if (!image.sourceFile || !image.generatedFileName) return;
            const imageUrl = URL.createObjectURL(image.sourceFile);
            const imageAnchor = document.createElement('a');
            imageAnchor.href = imageUrl;
            imageAnchor.download = image.generatedFileName;
            document.body.appendChild(imageAnchor);
            imageAnchor.click();
            imageAnchor.remove();
            URL.revokeObjectURL(imageUrl);
        });
    });
};
